import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { faLock, faUnlock, faSave, faTimes, faVideo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { getSystem } from '../../../api/actions/system/get-system';
import { saveSystem } from '../../../api/actions/system/save-system';
import { runVideoHealthCheck } from '../../../api/actions/system/system-video-health-check';
import { AddBreadcrumbsItem } from '../../../components/shared/breadcrumbs/breadcrumbs';
import { Button } from '../../../components/shared/button/button';
import { Grid } from '../../../components/shared/grid/grid';
import { LinkButton } from '../../../components/shared/link-button/link-button';
import { Loader } from '../../../components/shared/loader/loader';
import { compareText } from '../../../helpers/array-helpers';
import { basePerformError } from '../../../helpers/error-helpers';
import { joinPath } from '../../../helpers/path-helpers';
import { ISystem } from '../../../models/system';
import { toast } from 'react-toastify';

import styles from './system-view.module.scss';

export function SystemView(props: RouteComponentProps) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [runningHealthCheck, setRunningHealthCheck] = useState(false);
    const [system, setSystem] = useState<ISystem>();
    const [editingSystem, setEditingSystem] = useState<ISystem>();
    const [isEditing, setIsEditing] = useState(false);

    const loadData = React.useCallback(async () => {
        setLoading(true);
        try {
            setSystem(await getSystem());
        }
        catch(err) {
            basePerformError(err, props.history);
        }
        setLoading(false);
    }, [props.history]);

    const handleEdit = () => {
        if (system) {
            setEditingSystem({ ...system });
            setIsEditing(true);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditingSystem(undefined);
    };

    const handleSave = async () => {
        if (!editingSystem) return;
        
        setSaving(true);
        try {
            const savedSystem = await saveSystem(editingSystem);
            setIsEditing(false);
            setEditingSystem(undefined);
            loadData();
            toast.success('System settings saved successfully!');
        } catch (err) {
            basePerformError(err, props.history);
            toast.error('Failed to save system settings');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field: keyof ISystem, value: string | boolean) => {
        if (editingSystem) {
            setEditingSystem({
                ...editingSystem,
                [field]: value
            });
        }
    };

    const handleVideoHealthCheck = async () => {
        setRunningHealthCheck(true);
        try {
            await runVideoHealthCheck();
            toast.success('Video health check email sent to admins successfully!');
        } catch (err) {
            basePerformError(err, props.history);
            toast.error('Failed to send video health check email');
        } finally {
            setRunningHealthCheck(false);
        }
    };

    useEffect(() => {
        loadData().catch(() =>{/** */});
    }, [loadData]);

    return loading ? (<Loader />) : (<>
        <AddBreadcrumbsItem
            title="System"
            url={props.match.url}
        />

        <div className={styles.systemView}>
            <div className={styles.header}>
                <h2>System Settings</h2>
                <div className={styles.headerActions}>
                    <Button 
                        onClick={handleVideoHealthCheck} 
                        disabled={runningHealthCheck} 
                        className={styles.healthCheckButton}
                    >
                        <FontAwesomeIcon icon={faVideo} />
                        <span>{runningHealthCheck ? 'Running...' : 'Video Health Check'}</span>
                    </Button>
                    
                    {!isEditing ? (
                        <Button onClick={handleEdit} className={styles.editButton}>
                            <FontAwesomeIcon icon={faEdit} />
                            <span>Edit</span>
                        </Button>
                    ) : (
                        <div className={styles.actionButtons}>
                            <Button onClick={handleSave} disabled={saving} className={styles.saveButton}>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{saving ? 'Saving...' : 'Save'}</span>
                            </Button>
                            <Button onClick={handleCancel} disabled={saving} className={styles.cancelButton}>
                                <FontAwesomeIcon icon={faTimes} />
                                <span>Cancel</span>
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.settingsList}>
                <div className={styles.settingItem}>
                    <label>
                        <span className={styles.settingLabel}>Min Mobile Version:</span>
                        {isEditing ? (
                            <input
                                type="text"
                                value={editingSystem?.minMobileVersion || ''}
                                onChange={(e) => handleInputChange('minMobileVersion', e.target.value)}
                                placeholder="Enter version (e.g., 1.0.0)"
                                className={styles.input}
                            />
                        ) : (
                            <span className={styles.settingValue}>
                                {system?.minMobileVersion ? system.minMobileVersion : "None"}
                            </span>
                        )}
                    </label>
                </div>

                <div className={styles.settingItem}>
                    <label>
                        <span className={styles.settingLabel}>Mobile Update Link:</span>
                        {isEditing ? (
                            <input
                                type="url"
                                value={editingSystem?.mobileUpdateLink || ''}
                                onChange={(e) => handleInputChange('mobileUpdateLink', e.target.value)}
                                placeholder="Enter update link URL"
                                className={styles.input}
                            />
                        ) : (
                            <span className={styles.settingValue}>
                                {system?.mobileUpdateLink ? system.mobileUpdateLink : "None"}
                            </span>
                        )}
                    </label>
                </div>

                <div className={styles.settingItem}>
                    <label>
                        <span className={styles.settingLabel}>Mobile Paywall On:</span>
                        {isEditing ? (
                            <select
                                value={editingSystem?.mobilePaywallOn ? 'true' : 'false'}
                                onChange={(e) => handleInputChange('mobilePaywallOn', e.target.value === 'true')}
                                className={styles.select}
                            >
                                <option value="false">NO</option>
                                <option value="true">YES</option>
                            </select>
                        ) : (
                            <span className={styles.settingValue}>
                                {system?.mobilePaywallOn ? "YES" : "NO"}
                            </span>
                        )}
                    </label>
                </div>

                <div className={styles.settingItem}>
                    <label>
                        <span className={styles.settingLabel}>Mobile Paywall Optional:</span>
                        {isEditing ? (
                            <select
                                value={editingSystem?.mobilePaymentOptional ? 'true' : 'false'}
                                onChange={(e) => handleInputChange('mobilePaymentOptional', e.target.value === 'true')}
                                className={styles.select}
                            >
                                <option value="false">NO</option>
                                <option value="true">YES</option>
                            </select>
                        ) : (
                            <span className={styles.settingValue}>
                                {system?.mobilePaymentOptional ? "YES" : "NO"}
                            </span>
                        )}
                    </label>
                </div>

               {system?.mobilePaymentOptional && (<>
                <div className={styles.settingItem}>
                    <label>
                        <span className={styles.settingLabel}>Number of Sessions Before Optional Paywall:</span>
                    </label>
                    {isEditing ? (
                        <input
                            type="number"
                            value={editingSystem?.numberOfSessionsBeforePaywall || ''}
                            onChange={(e) => handleInputChange('numberOfSessionsBeforePaywall', e.target.value)}
                            placeholder="Enter number of sessions"
                            className={styles.input}
                        />
                    ) : (
                        <span className={styles.settingValue}>
                            {system?.numberOfSessionsBeforePaywall ? system.numberOfSessionsBeforePaywall : 0}
                        </span>
                    )}
                </div>

                <div className={styles.settingItem}>
                    <label>
                        <span className={styles.settingLabel}>Mobile Payment Optional Prompt Delay Period (Days):</span>
                    </label>
                    {isEditing ? (
                        <input
                            type="number"
                            value={editingSystem?.paymentOptionalPausePeriod || ''}
                            onChange={(e) => handleInputChange('paymentOptionalPausePeriod', e.target.value)}
                            placeholder="Enter pause period"
                            className={styles.input}
                        />
                    ) : (
                        <span className={styles.settingValue}>
                            {system?.paymentOptionalPausePeriod ? system.paymentOptionalPausePeriod : "None"}
                        </span>
                    )}
                </div>
                </>)}

                <div className={styles.settingItem}>
                    <label>
                        <span className={styles.settingLabel}>Organization Support On:</span>
                   
                {isEditing ? (
                    <select
                        value={editingSystem?.orgSupportOn ? 'true' : 'false'}
                        onChange={(e) => handleInputChange('orgSupportOn', e.target.value === 'true')}
                        className={styles.select}
                    >
                        <option value="false">NO</option>
                        <option value="true">YES</option>
                    </select>
                ) : (
                    <span className={styles.settingValue}>
                        {system?.orgSupportOn ? "YES" : "NO"}
                    </span>
                )}
                </label>
                </div>

            </div>
        </div>
    </>);
}
